/**
 * Abby Home — visual (photo) search.
 *
 * Everything here runs in the shopper's browser. There is no server-side
 * model and nothing is uploaded anywhere: a photo is turned into a small
 * set of numbers (a "feature vector" describing its color and shape) and
 * compared against the same numbers computed for every product photo.
 * Products whose numbers are closest win.
 *
 * The product catalog itself is fetched from a small JSON endpoint
 * (a Shopify page rendered by Liquid — see page.abby-visual-search-data.liquid)
 * so it always reflects the live catalog with no separate index to maintain.
 */
(function () {
  'use strict';

  var FEATURE_SIZE = 96; // every image is stretched to FEATURE_SIZE x FEATURE_SIZE before analysis
  var GRID = 4; // spatial grids are GRID x GRID cells
  var CELL = FEATURE_SIZE / GRID;
  var HUE_BINS = 16;
  var SAT_BINS = 8;
  var VAL_BINS = 8;
  var VECTOR_LENGTH = HUE_BINS + SAT_BINS + VAL_BINS + (GRID * GRID * 3) + (GRID * GRID); // 96

  var INDEX_CACHE_KEY = 'abbyVisualSearchIndex_v1';
  var INDEX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // rebuild at least once a week even if the count hasn't changed
  var RESULT_COUNT = 12;
  var FETCH_CONCURRENCY = 6;
  var CATEGORY_MATCH_BOOST = 0.15;

  // ---- "Similar category" fallback for photos of items we don't stock ----
  // The 96-number color/shape descriptor above is good at finding visually
  // close matches, but has no idea what an object actually IS — so a photo
  // of something we don't carry (e.g. a mug style we don't have) can score
  // low against everything, or worse, latch onto an unrelated item that
  // merely shares its colors. When the best visual match is weak, we lazy
  // load a small general-purpose image classifier (nothing downloaded for
  // an ordinary, already-matching search) to guess the general category and
  // show real in-stock items from it instead — visually different, but at
  // least the right kind of thing.
  //
  // Confidence is judged RELATIVE to this query's own score spread, not a
  // fixed number. Every value that goes into the feature vector above is
  // non-negative (histogram counts, per-cell color averages, edge
  // magnitudes), so the raw score between any two product photos already
  // tends to sit fairly high just from sharing a plain white-background
  // product-photo style — a fixed cutoff can't tell "genuinely the same
  // kind of item" apart from "coincidentally similar backdrop", which is
  // why a flat threshold here let a spatula set win as the "best match"
  // for a photo of a thermos. Instead we check whether the best match is a
  // real standout — how many standard deviations above the rest of the
  // catalog it scores (see rankByBestMatchThenSimilarity) — since a
  // genuine match should look distinctly closer than the pack, not merely
  // similar to everything the way an unrelated item does.
  // VISUAL_MATCH_MIN_SCORE is a low sanity floor underneath that, for the
  // rare case where every score is clustered near zero and a z-score alone
  // could be misleading. Both are estimates, not measured constants — real
  // shopper photos are the only way to tune them properly.
  var VISUAL_MATCH_ZSCORE_THRESHOLD = 1.8;
  var VISUAL_MATCH_MIN_SCORE = 0.35;
  var AI_MODEL_VERSION = 1;
  var AI_MODEL_ALPHA = 0.25; // smallest MobileNet variant (~1-2MB) — this only ever loads on shopper data connections
  var AI_TFJS_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js';
  var AI_MOBILENET_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js';
  var CATEGORY_RESULT_COUNT = 12;
  var CATEGORY_FETCH_MULTIPLIER = 2; // fetch extra candidates so re-ranking by visual similarity has something to work with
  var AI_CATEGORY_TOPK = 10; // look at the classifier's top 10 guesses, not just its #1 pick — see mapPredictionsToCategory
  var AI_CATEGORY_MIN_CONFIDENCE = 0.15; // below this, admit we don't recognize the photo rather than guess

  // Keyword → { real collection handle, specific display label }, grouped
  // by collection and checked in priority order within each prediction
  // (first match wins for that one prediction — see mapPredictionsToCategory
  // for how multiple predictions are combined). Grounded in this store's
  // actual collections rather than the "Product type" field, which is blank
  // on ~98% of the catalog and unusable as a signal. The label is shown to
  // the shopper and is deliberately more specific than the collection name
  // itself (e.g. "cast-iron skillets", not "cookware") — collection buckets
  // exist for inventory routing, not because "cookware" is what anyone
  // actually asked to see. Within each collection, more specific phrases
  // are listed before the generic terms they'd otherwise be swallowed by
  // (e.g. "water bottle" before plain "bottle").
  var AI_CATEGORY_MAP = [
    { collection: 'water-bottle-tumbler', matches: [
      { keyword: 'tumbler', label: 'tumblers' },
      { keyword: 'water bottle', label: 'water bottles' },
      { keyword: 'sports bottle', label: 'sports bottles' }
    ] },
    { collection: 'mugs', matches: [
      { keyword: 'coffee mug', label: 'mugs' },
      { keyword: 'mug', label: 'mugs' },
      { keyword: 'teacup', label: 'teacups' },
      { keyword: 'cup', label: 'cups' },
      { keyword: 'espresso', label: 'espresso cups' }
    ] },
    { collection: 'flasks', matches: [
      { keyword: 'flask', label: 'flasks' },
      { keyword: 'thermos', label: 'thermoses' },
      { keyword: 'water jug', label: 'water jugs' },
      { keyword: 'jug', label: 'jugs' },
      { keyword: 'pitcher', label: 'pitchers' },
      { keyword: 'bottle', label: 'bottles' }
    ] },
    { collection: 'glass', matches: [
      { keyword: 'wine glass', label: 'wine glasses' },
      { keyword: 'goblet', label: 'goblets' },
      { keyword: 'vase', label: 'vases' },
      { keyword: 'drinking glass', label: 'glassware' },
      { keyword: 'glass', label: 'glassware' }
    ] },
    { collection: 'table-serve', matches: [
      { keyword: 'soup bowl', label: 'bowls' },
      { keyword: 'bowl', label: 'bowls' },
      { keyword: 'plate rack', label: 'plates' },
      { keyword: 'plate', label: 'plates' },
      { keyword: 'teapot', label: 'teapots' },
      { keyword: 'tray', label: 'trays' },
      { keyword: 'platter', label: 'platters' },
      { keyword: 'saucer', label: 'saucers' },
      { keyword: 'dish', label: 'dishes' }
    ] },
    { collection: 'cookware', matches: [
      { keyword: 'frying pan', label: 'frying pans' },
      { keyword: 'saucepan', label: 'saucepans' },
      { keyword: 'wok', label: 'woks' },
      { keyword: 'kettle', label: 'kettles' },
      { keyword: 'dutch oven', label: 'dutch ovens' },
      { keyword: 'skillet', label: 'cast-iron skillets' },
      { keyword: 'crock pot', label: 'pots' },
      { keyword: 'caldron', label: 'pots' },
      { keyword: 'cauldron', label: 'pots' },
      { keyword: 'pot', label: 'pots' },
      { keyword: 'pan', label: 'pans' }
    ] },
    { collection: 'cutlery', matches: [
      { keyword: 'cutlery', label: 'cutlery sets' },
      { keyword: 'knife', label: 'knives' },
      { keyword: 'fork', label: 'forks' },
      { keyword: 'spoon', label: 'spoons' },
      { keyword: 'whisk', label: 'whisks' },
      { keyword: 'spatula', label: 'spatulas' },
      { keyword: 'ladle', label: 'ladles' }
    ] },
    { collection: 'bed-bath', matches: [
      { keyword: 'bath towel', label: 'bath towels' },
      { keyword: 'towel', label: 'towels' },
      { keyword: 'blanket', label: 'blankets' },
      { keyword: 'pillow', label: 'pillows' },
      { keyword: 'bedding', label: 'bedding' },
      { keyword: 'quilt', label: 'quilts' }
    ] },
    { collection: 'appliances', matches: [
      { keyword: 'toaster', label: 'toasters' },
      { keyword: 'blender', label: 'blenders' },
      { keyword: 'mixer', label: 'mixers' },
      { keyword: 'microwave', label: 'microwaves' },
      { keyword: 'espresso machine', label: 'espresso machines' },
      { keyword: 'waffle iron', label: 'waffle irons' },
      { keyword: 'rotisserie', label: 'appliances' },
      { keyword: 'appliance', label: 'appliances' }
    ] },
    { collection: 'ceramic', matches: [
      { keyword: 'ceramic', label: 'ceramic pieces' },
      { keyword: 'pottery', label: 'pottery' },
      { keyword: 'porcelain', label: 'porcelain pieces' },
      { keyword: 'earthenware', label: 'earthenware' }
    ] },
    { collection: 'kitchen-gadgets', matches: [
      { keyword: 'colander', label: 'colanders' },
      { keyword: 'strainer', label: 'strainers' },
      { keyword: 'can opener', label: 'can openers' },
      { keyword: 'corkscrew', label: 'corkscrews' },
      { keyword: 'grater', label: 'graters' },
      { keyword: 'rack', label: 'racks' },
      { keyword: 'basket', label: 'baskets' },
      { keyword: 'gadget', label: 'kitchen gadgets' }
    ] }
  ];

  // ---- Live camera "hold still" auto-detect ----
  // No object-recognition model runs here (consistent with the rest of this
  // feature: no server, nothing uploaded, nothing heavy downloaded). It only
  // (a) notices when the camera stops moving and (b) makes a rough guess at
  // the framing box by comparing the frame against an estimated background,
  // so it can draw a focus box and snap the photo automatically once the
  // shot looks steady — the same idea as a phone camera's autofocus box,
  // without claiming to know what the product actually is.
  var CAMERA_ANALYSIS_W = 60;
  var CAMERA_ANALYSIS_H = 80; // matches the 3:4 viewport aspect ratio in the CSS
  var CAMERA_TICK_MS = 150;
  var CAMERA_STILL_FRAMES_NEEDED = 5; // ~750ms of steady framing before auto-capture
  var CAMERA_STILL_FRAMES_FALLBACK = 14; // ~2.1s of steady framing when no foreground box was ever found (see cameraTick) — a low-contrast shot (e.g. white dinnerware on a light surface, or a product filling the whole frame) can never produce a box under this heuristic, so this guarantees a capture still fires instead of the scan hanging forever
  var CAMERA_STILL_DIFF_THRESHOLD = 0.02;
  var CAMERA_FOREGROUND_DIFF = 0.12;
  var CAMERA_MIN_FOREGROUND_FRACTION = 0.04;

  var scratchCanvas = null;
  var scratchCtx = null;

  function getScratchContext() {
    if (!scratchCanvas) {
      scratchCanvas = document.createElement('canvas');
      scratchCanvas.width = FEATURE_SIZE;
      scratchCanvas.height = FEATURE_SIZE;
      scratchCtx = scratchCanvas.getContext('2d', { willReadFrequently: true });
    }
    return scratchCtx;
  }

  // Stretches (not crops) any image into a FEATURE_SIZE x FEATURE_SIZE pixel buffer.
  function toFeatureImageData(imgEl) {
    var ctx = getScratchContext();
    ctx.clearRect(0, 0, FEATURE_SIZE, FEATURE_SIZE);
    ctx.drawImage(imgEl, 0, 0, FEATURE_SIZE, FEATURE_SIZE);
    return ctx.getImageData(0, 0, FEATURE_SIZE, FEATURE_SIZE);
  }

  // The core descriptor: a 96-number fingerprint of an image's color and shape.
  //   - 32 numbers: a color histogram (hue/saturation/value) — the overall palette
  //   - 48 numbers: average color in a 4x4 grid over the image — where colors sit
  //   - 16 numbers: average edge strength in the same 4x4 grid — rough shape/texture
  // The whole vector is L2-normalized, so comparing two vectors is a single dot product.
  function computeFeatureVector(imageData) {
    var data = imageData.data;
    var size = FEATURE_SIZE;
    var total = size * size;

    var hueHist = new Float64Array(HUE_BINS);
    var satHist = new Float64Array(SAT_BINS);
    var valHist = new Float64Array(VAL_BINS);
    var colorSum = new Float64Array(GRID * GRID * 3);
    var colorCount = new Float64Array(GRID * GRID);
    var luma = new Float64Array(total);

    for (var y = 0; y < size; y++) {
      for (var x = 0; x < size; x++) {
        var px = y * size + x;
        var idx = px * 4;
        var r = data[idx] / 255;
        var g = data[idx + 1] / 255;
        var b = data[idx + 2] / 255;

        var maxc = Math.max(r, g, b);
        var minc = Math.min(r, g, b);
        var delta = maxc - minc;

        var h = 0;
        if (delta > 0) {
          if (maxc === r) {
            h = 60 * (((g - b) / delta) % 6);
          } else if (maxc === g) {
            h = 60 * (((b - r) / delta) + 2);
          } else {
            h = 60 * (((r - g) / delta) + 4);
          }
        }
        if (h < 0) h += 360;
        var s = maxc === 0 ? 0 : delta / maxc;
        var v = maxc;

        var hueBin = Math.min(HUE_BINS - 1, Math.floor((h / 360) * HUE_BINS));
        var satBin = Math.min(SAT_BINS - 1, Math.floor(s * SAT_BINS));
        var valBin = Math.min(VAL_BINS - 1, Math.floor(v * VAL_BINS));
        hueHist[hueBin] += 1;
        satHist[satBin] += 1;
        valHist[valBin] += 1;

        var gx = Math.min(GRID - 1, Math.floor(x / CELL));
        var gy = Math.min(GRID - 1, Math.floor(y / CELL));
        var cell = gy * GRID + gx;
        colorSum[cell * 3] += r;
        colorSum[cell * 3 + 1] += g;
        colorSum[cell * 3 + 2] += b;
        colorCount[cell] += 1;

        luma[px] = (0.299 * r) + (0.587 * g) + (0.114 * b);
      }
    }

    var edgeSum = new Float64Array(GRID * GRID);
    for (y = 0; y < size; y++) {
      for (x = 0; x < size; x++) {
        var xm1 = x > 0 ? x - 1 : x;
        var xp1 = x < size - 1 ? x + 1 : x;
        var ym1 = y > 0 ? y - 1 : y;
        var yp1 = y < size - 1 ? y + 1 : y;
        var gxMag = luma[(y * size) + xp1] - luma[(y * size) + xm1];
        var gyMag = luma[(yp1 * size) + x] - luma[(ym1 * size) + x];
        var mag = Math.sqrt((gxMag * gxMag) + (gyMag * gyMag));

        var gxCell = Math.min(GRID - 1, Math.floor(x / CELL));
        var gyCell = Math.min(GRID - 1, Math.floor(y / CELL));
        edgeSum[(gyCell * GRID) + gxCell] += mag;
      }
    }

    var vec = new Float64Array(VECTOR_LENGTH);
    var o = 0;
    var i;
    for (i = 0; i < HUE_BINS; i++) vec[o++] = hueHist[i] / total;
    for (i = 0; i < SAT_BINS; i++) vec[o++] = satHist[i] / total;
    for (i = 0; i < VAL_BINS; i++) vec[o++] = valHist[i] / total;
    for (i = 0; i < GRID * GRID; i++) {
      var n = colorCount[i] || 1;
      vec[o++] = colorSum[i * 3] / n;
      vec[o++] = colorSum[i * 3 + 1] / n;
      vec[o++] = colorSum[i * 3 + 2] / n;
    }
    for (i = 0; i < GRID * GRID; i++) {
      var cn = colorCount[i] || 1;
      vec[o++] = edgeSum[i] / cn;
    }

    var norm = 0;
    for (i = 0; i < VECTOR_LENGTH; i++) norm += vec[i] * vec[i];
    norm = Math.sqrt(norm) || 1;
    var out = new Array(VECTOR_LENGTH);
    for (i = 0; i < VECTOR_LENGTH; i++) out[i] = vec[i] / norm;
    return out;
  }

  function dotProduct(a, b) {
    var s = 0;
    for (var i = 0; i < a.length; i++) s += a[i] * b[i];
    return s;
  }

  // Ranks the catalog against a query photo's feature vector. The single
  // best match is whichever item is closest to the query photo; every
  // result after that is instead ranked by how close it is to THAT best
  // match (with a small bonus for sharing its product type, once the index
  // carries that field) so the list reads as "the item, then things like
  // it" rather than "a dozen things that each happen to share some color or
  // shape with the photo you took".
  //
  // Also returns bestZScore: how many standard deviations the best score
  // sits above the mean score across the WHOLE catalog for this query.
  // Because the raw scores here don't have a stable absolute scale (see
  // VISUAL_MATCH_ZSCORE_THRESHOLD above), whether the top match is a real
  // outlier relative to this specific query's own score spread is a much
  // more reliable "is this actually a match" signal than the raw score
  // itself.
  function rankByBestMatchThenSimilarity(items, queryVec) {
    if (!items.length) return { results: [], bestZScore: 0, bestScore: 0 };

    var byQuery = items.map(function (it) { return { it: it, score: dotProduct(queryVec, it.vec) }; });
    byQuery.sort(function (a, b) { return b.score - a.score; });
    var best = byQuery[0];

    var sum = 0;
    for (var s = 0; s < byQuery.length; s++) sum += byQuery[s].score;
    var mean = sum / byQuery.length;
    var variance = 0;
    for (var v = 0; v < byQuery.length; v++) {
      var diff = byQuery[v].score - mean;
      variance += diff * diff;
    }
    var std = Math.sqrt(variance / byQuery.length) || 0.0001;
    var bestZScore = (best.score - mean) / std;

    var rest = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it === best.it) continue;
      var score = dotProduct(best.it.vec, it.vec);
      if (best.it.type && it.type && it.type === best.it.type) {
        score += CATEGORY_MATCH_BOOST;
      }
      rest.push({ it: it, score: score });
    }
    rest.sort(function (a, b) { return b.score - a.score; });

    var results = [{ it: best.it, score: best.score, isBest: true }];
    for (var j = 0; j < rest.length && results.length < RESULT_COUNT; j++) {
      results.push({ it: rest[j].it, score: rest[j].score, isBest: false });
    }
    return { results: results, bestZScore: bestZScore, bestScore: best.score };
  }

  function loadImageElement(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Image failed to load: ' + url)); };
      img.src = url;
    });
  }

  function loadCachedIndex() {
    try {
      var raw = window.localStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items) || typeof parsed.total !== 'number') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveCachedIndex(total, items) {
    try {
      window.localStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({
        total: total,
        builtAt: Date.now(),
        items: items
      }));
    } catch (e) {
      // Storage full/unavailable (private browsing, quota, etc.) — the feature still
      // works, it will just rebuild the index every time it's used.
      console.warn('[Abby visual search] could not cache the index locally:', e);
    }
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Catalog request failed: ' + res.status);
      return res.json();
    });
  }

  // Downloads a small thumbnail for every product and computes its feature vector,
  // with a handful of downloads in flight at once. A product whose image fails to
  // load is skipped rather than failing the whole index.
  function buildIndexFromCatalog(products, onProgress) {
    var results = [];
    var next = 0;
    var done = 0;
    var failed = 0;

    function worker() {
      return Promise.resolve().then(function step() {
        if (next >= products.length) return;
        var product = products[next++];
        return loadImageElement(product.image).then(function (img) {
          var vec = computeFeatureVector(toFeatureImageData(img));
          results.push({
            id: product.id,
            title: product.title,
            handle: product.handle,
            price: product.price,
            image: product.image,
            type: product.type,
            vec: vec
          });
        }).catch(function (err) {
          failed++;
          console.warn('[Abby visual search] skipping a product (image failed):', product.handle, err);
        }).then(function () {
          done++;
          if (onProgress) onProgress(done, products.length, failed);
          return step();
        });
      });
    }

    var workers = [];
    var workerCount = Math.min(FETCH_CONCURRENCY, products.length) || 1;
    for (var w = 0; w < workerCount; w++) workers.push(worker());
    return Promise.all(workers).then(function () { return { items: results, failed: failed }; });
  }

  function fetchFullCatalog(endpointUrl, firstPage) {
    var products = firstPage.products.slice();
    var pages = firstPage.pages || 1;
    if (pages <= 1) return Promise.resolve(products);

    var fetches = [];
    for (var p = 2; p <= pages; p++) {
      fetches.push(fetchJson(endpointUrl + '?page=' + p));
    }
    return Promise.all(fetches).then(function (rest) {
      rest.forEach(function (page) { products = products.concat(page.products); });
      return products;
    });
  }

  var inFlightIndex = null;

  // Derives another static asset's URL from the main static index URL by swapping
  // in a different filename suffix before the ".json" extension (and before any
  // "?v=..." cache-busting query string that asset_url may have appended), so a
  // single `data-static-index-url` attribute is enough to locate the whole
  // multi-part index below.
  function deriveIndexUrl(staticUrl, suffix) {
    return staticUrl.replace(/\.json(?=(\?|$))/, suffix + '.json');
  }

  // A precomputed index (built once, ahead of time, via the internal index-builder
  // page and shipped as static theme assets) means an ordinary shopper's browser
  // never has to download and analyze every product photo itself. Used only when
  // it exists and its product count still matches the live catalog; otherwise we
  // fall back to building it live, same as before.
  //
  // The index is stored as a small meta file (data-static-index-url with "-meta"
  // spliced in) giving the declared total and how many numbered part files there
  // are, plus that many part files (same URL with "-p01", "-p02", ... spliced in),
  // each a plain JSON array of items. It's split up because a single JSON file
  // covering the whole catalog is too large to write reliably in one piece; the
  // parts are fetched in parallel here and stitched back into one array.
  function fetchStaticIndex(staticUrl, expectedTotal) {
    if (!staticUrl) return Promise.resolve(null);
    var metaUrl = deriveIndexUrl(staticUrl, '-meta');
    return fetchJson(metaUrl).then(function (meta) {
      if (!meta || typeof meta.total !== 'number' || typeof meta.parts !== 'number' || meta.parts < 1) return null;
      if (meta.total !== expectedTotal) return null; // catalog changed since this was built — treat as stale

      var partFetches = [];
      for (var i = 1; i <= meta.parts; i++) {
        var partSuffix = '-p' + (i < 10 ? '0' + i : String(i));
        partFetches.push(fetchJson(deriveIndexUrl(staticUrl, partSuffix)));
      }

      return Promise.all(partFetches).then(function (parts) {
        var items = [];
        for (var p = 0; p < parts.length; p++) {
          if (!Array.isArray(parts[p])) return null;
          items = items.concat(parts[p]);
        }
        if (items.length !== meta.total) return null; // a part came back short/broken — treat as stale
        return items;
      });
    }).catch(function () {
      return null; // no static index yet, or it failed to load — that's fine, we fall back
    });
  }

  // Returns a ready-to-use array of {id, title, handle, price, image, vec}. Prefers
  // a precomputed static index (see above); otherwise uses a cached copy from
  // localStorage when the catalog's product count hasn't changed and the cache
  // isn't too old; otherwise rebuilds (downloading a small thumbnail per product
  // in this browser) and re-caches.
  function ensureIndex(endpointUrl, staticIndexUrl, onProgress) {
    if (inFlightIndex) return inFlightIndex;

    inFlightIndex = fetchJson(endpointUrl).then(function (firstPage) {
      return fetchStaticIndex(staticIndexUrl, firstPage.total).then(function (staticItems) {
        if (staticItems) return staticItems;

        var cached = loadCachedIndex();
        var isFresh = cached &&
          cached.total === firstPage.total &&
          (Date.now() - cached.builtAt) < INDEX_MAX_AGE_MS;

        if (isFresh) return cached.items;

        return fetchFullCatalog(endpointUrl, firstPage).then(function (products) {
          return buildIndexFromCatalog(products, onProgress).then(function (result) {
            saveCachedIndex(firstPage.total, result.items);
            return result.items;
          });
        });
      });
    }).catch(function (err) {
      var cached = loadCachedIndex();
      if (cached) return cached.items; // offline or the endpoint hiccupped — use what we have
      throw err;
    });

    inFlightIndex.catch(function () { inFlightIndex = null; }); // allow retrying after a hard failure

    return inFlightIndex;
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value === null || value === undefined ? '' : String(value);
    return div.innerHTML;
  }

  // ---- AI category-fallback helpers ----

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') { resolve(); return; }
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () { script.setAttribute('data-loaded', 'true'); resolve(); };
      script.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(script);
    });
  }

  var aiModelPromise = null;

  // Lazily loads TensorFlow.js + the MobileNet classifier and resolves with
  // a ready-to-use model. Only ever called from the fallback path, so an
  // ordinary shopper whose photo matches something in stock never downloads
  // any of this. Cached across searches so it only loads once per visit.
  function ensureAiModel() {
    if (aiModelPromise) return aiModelPromise;
    aiModelPromise = Promise.resolve()
      .then(function () {
        if (window.tf && window.mobilenet) return null;
        return loadScriptOnce(AI_TFJS_URL).then(function () { return loadScriptOnce(AI_MOBILENET_URL); });
      })
      .then(function () {
        return window.mobilenet.load({ version: AI_MODEL_VERSION, alpha: AI_MODEL_ALPHA });
      })
      .catch(function (err) {
        aiModelPromise = null; // allow retrying on a later search rather than staying broken all session
        throw err;
      });
    return aiModelPromise;
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  var keywordRegexCache = {};
  // Whole-word/phrase match rather than a raw substring test — a plain
  // indexOf let "dish" match inside "dishwasher" and would let "pot" match
  // inside "flowerpot". A word-boundary regex only fires on the actual word,
  // cached per keyword since the same keyword list is tested against every
  // prediction.
  function keywordMatches(label, keyword) {
    var re = keywordRegexCache[keyword];
    if (!re) {
      re = new RegExp('\\b' + escapeRegExp(keyword) + '\\b', 'i');
      keywordRegexCache[keyword] = re;
    }
    return re.test(label);
  }

  // Looks at ALL of the classifier's top guesses (not just its #1 pick) and
  // tallies their probabilities by which real collection they'd map to, so
  // several plausible-but-not-top-ranked guesses agreeing with each other
  // can outvote one confidently-wrong outlier. MobileNet is a small,
  // general-purpose model with no idea this is a home-goods catalog, and it
  // occasionally mislabels an item as some superficially similar ImageNet
  // class (a stack of bowls read as "crock pot", say) — if three lower-
  // ranked guesses like "soup bowl", "cup" and "plate" also showed up, they
  // should collectively outweigh that one higher-ranked but wrong guess.
  // Each individual PREDICTION still only votes for one collection — the
  // first (most specific) keyword match within it, in AI_CATEGORY_MAP's own
  // priority order — so a label like "water bottle" votes for water bottles
  // specifically rather than splitting its vote with the generic "bottle"
  // keyword under flasks.
  //
  // Returns { collection, label } for the winner, or null if nothing clears
  // AI_CATEGORY_MIN_CONFIDENCE — an honest "couldn't tell what this is"
  // beats a confident wrong guess every time.
  function mapPredictionsToCategory(predictions) {
    if (!predictions || !predictions.length) return null;

    var collectionScore = {};
    var bestLabelForCollection = {};
    var bestLabelScore = {};

    for (var i = 0; i < predictions.length; i++) {
      var label = (predictions[i].className || '').toLowerCase();
      var prob = typeof predictions[i].probability === 'number' ? predictions[i].probability : 0;
      if (!label || prob <= 0) continue;

      for (var j = 0; j < AI_CATEGORY_MAP.length; j++) {
        var entry = AI_CATEGORY_MAP[j];
        var matched = null;
        for (var k = 0; k < entry.matches.length; k++) {
          if (keywordMatches(label, entry.matches[k].keyword)) { matched = entry.matches[k]; break; }
        }
        if (!matched) continue;

        collectionScore[entry.collection] = (collectionScore[entry.collection] || 0) + prob;
        if (!(entry.collection in bestLabelScore) || prob > bestLabelScore[entry.collection]) {
          bestLabelScore[entry.collection] = prob;
          bestLabelForCollection[entry.collection] = matched.label;
        }
        break; // this prediction has cast its one vote
      }
    }

    var winner = null;
    var winnerScore = 0;
    for (var collection in collectionScore) {
      if (collectionScore[collection] > winnerScore) {
        winnerScore = collectionScore[collection];
        winner = collection;
      }
    }

    if (!winner || winnerScore < AI_CATEGORY_MIN_CONFIDENCE) return null;
    return { collection: winner, label: bestLabelForCollection[winner] };
  }

  // Formats a decimal-string amount (as returned by the public collection
  // JSON endpoint, e.g. "24.99") using the shop's currency. This is a
  // best-effort client-side approximation of the theme's own money_format
  // and may not pixel-match prices rendered by Liquid elsewhere on the site.
  function formatRawPrice(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'RWF';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(n);
    } catch (e) {
      return currency + ' ' + Math.round(n).toLocaleString();
    }
  }

  // Shopify's public, unauthenticated collection JSON endpoint — no app or
  // backend required. Used only for the "similar category" fallback, so a
  // failure here (network hiccup, endpoint disabled) just means the
  // fallback doesn't find anything and we fall back again to the plain
  // visual-only results.
  function fetchCollectionProducts(rootUrl, handle, limit) {
    var url = rootUrl + '/collections/' + handle + '/products.json?limit=' + (limit || CATEGORY_RESULT_COUNT);
    return fetchJson(url).then(function (data) {
      return (data && data.products) || [];
    });
  }

  function normalizeShopifyProduct(product) {
    var image = product.images && product.images.length ? (product.images[0].src || product.images[0]) : '';
    var variant = (product.variants && product.variants[0]) || {};
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      price: formatRawPrice(variant.price),
      image: image
    };
  }

  // ---- Live camera helpers (pure functions, no DOM/class state) ----

  // Same math a CSS `object-fit: cover` uses: the crop rectangle, in source
  // pixels, that fills a box of the given target aspect ratio. Used so the
  // small analysis buffer sees exactly the same crop the shopper sees on
  // screen, and framing-box coordinates map 1:1 onto the visible viewport.
  function coverCropRect(srcW, srcH, targetAspect) {
    var srcAspect = srcW / srcH;
    var sx, sy, sw, sh;
    if (srcAspect > targetAspect) {
      sh = srcH;
      sw = srcH * targetAspect;
      sx = (srcW - sw) / 2;
      sy = 0;
    } else {
      sw = srcW;
      sh = srcW / targetAspect;
      sx = 0;
      sy = (srcH - sh) / 2;
    }
    return { sx: sx, sy: sy, sw: sw, sh: sh };
  }

  function frameLuma(ctx, w, h) {
    var data = ctx.getImageData(0, 0, w, h).data;
    var n = w * h;
    var luma = new Float64Array(n);
    for (var i = 0; i < n; i++) {
      var o = i * 4;
      luma[i] = (0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2]) / 255;
    }
    return luma;
  }

  function meanAbsDiff(a, b) {
    var sum = 0;
    for (var i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
    return sum / a.length;
  }

  // Rough "what's the product" guess: assume the frame's outer edge is
  // mostly background, estimate its brightness, then find the bounding box
  // of pixels that differ from it by more than a threshold. This is a
  // framing heuristic, not object recognition — it has no idea what a
  // product IS, only that something in the middle of the frame looks
  // different from its border.
  function estimateForegroundBox(luma, w, h) {
    var marginX = Math.max(2, Math.round(w * 0.08));
    var marginY = Math.max(2, Math.round(h * 0.08));
    var bgSum = 0;
    var bgCount = 0;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (x < marginX || x >= w - marginX || y < marginY || y >= h - marginY) {
          bgSum += luma[y * w + x];
          bgCount++;
        }
      }
    }
    var bg = bgCount ? bgSum / bgCount : 0.5;

    var mask = new Uint8Array(w * h);
    var total = 0;
    for (var i = 0; i < w * h; i++) {
      if (Math.abs(luma[i] - bg) > CAMERA_FOREGROUND_DIFF) {
        mask[i] = 1;
        total++;
      }
    }
    if (total < w * h * CAMERA_MIN_FOREGROUND_FRACTION) return null;

    var colCount = new Int32Array(w);
    var rowCount = new Int32Array(h);
    for (var y2 = 0; y2 < h; y2++) {
      for (var x2 = 0; x2 < w; x2++) {
        if (mask[y2 * w + x2]) {
          colCount[x2]++;
          rowCount[y2]++;
        }
      }
    }

    var colThreshold = Math.max(1, h * 0.06);
    var rowThreshold = Math.max(1, w * 0.06);
    var minX = w, maxX = -1, minY = h, maxY = -1;
    for (var x3 = 0; x3 < w; x3++) {
      if (colCount[x3] >= colThreshold) {
        if (x3 < minX) minX = x3;
        if (x3 > maxX) maxX = x3;
      }
    }
    for (var y3 = 0; y3 < h; y3++) {
      if (rowCount[y3] >= rowThreshold) {
        if (y3 < minY) minY = y3;
        if (y3 > maxY) maxY = y3;
      }
    }
    if (maxX < minX || maxY < minY) return null;

    var padX = ((maxX - minX) * 0.08) + 1;
    var padY = ((maxY - minY) * 0.08) + 1;
    minX = Math.max(0, minX - padX);
    maxX = Math.min(w - 1, maxX + padX);
    minY = Math.max(0, minY - padY);
    maxY = Math.min(h - 1, maxY + padY);

    return { x0: minX / w, y0: minY / h, x1: (maxX + 1) / w, y1: (maxY + 1) / h };
  }

  function positionBracket(el, x, y) {
    if (el) el.style.transform = 'translate(' + Math.round(x) + 'px,' + Math.round(y) + 'px)';
  }

  class VisualSearchModal extends HTMLElement {
    connectedCallback() {
      this.rootUrl = (this.getAttribute('data-root-url') || '').replace(/\/$/, '');
      this.endpointUrl = this.getAttribute('data-endpoint-url');
      this.staticIndexUrl = this.getAttribute('data-static-index-url');

      this.dropzone = this.querySelector('[data-visual-search-dropzone]');
      this.fileInput = this.querySelector('[data-visual-search-file]');
      this.previewWrap = this.querySelector('[data-visual-search-preview]');
      this.previewImg = this.querySelector('[data-visual-search-preview-img]');
      this.previewLoadingEl = this.querySelector('[data-visual-search-preview-loading]');
      this.retakeBtn = this.querySelector('[data-visual-search-retake]');
      this.statusEl = this.querySelector('[data-visual-search-status]');
      this.resultsEl = this.querySelector('[data-visual-search-results]');

      this.cameraSection = this.querySelector('[data-visual-search-camera]');
      this.cameraViewportEl = this.querySelector('[data-visual-search-camera-viewport]');
      this.videoEl = this.querySelector('[data-visual-search-video]');
      this.cameraHintEl = this.querySelector('[data-visual-search-camera-hint]');
      this.cameraErrorEl = this.querySelector('[data-visual-search-camera-error]');
      this.openCameraBtn = this.querySelector('[data-visual-search-open-camera]');
      this.cameraCancelBtn = this.querySelector('[data-visual-search-camera-cancel]');
      this.cameraShutterBtn = this.querySelector('[data-visual-search-camera-shutter]');
      this.libraryLabelEl = this.querySelector('[data-visual-search-library-label]');
      this.brackets = {
        tl: this.querySelector('[data-visual-search-bracket="tl"]'),
        tr: this.querySelector('[data-visual-search-bracket="tr"]'),
        bl: this.querySelector('[data-visual-search-bracket="bl"]'),
        br: this.querySelector('[data-visual-search-bracket="br"]')
      };

      this.cameraStream = null;
      this.cameraTimer = null;
      this.cameraAnalysisCtx = null;
      this.cameraPrevLuma = null;
      this.cameraStillCount = 0;
      this.cameraLocked = false;

      // Identifies the current search so a superseded one's late-arriving
      // results can never overwrite it, and holds the object URL behind the
      // current preview image so it can be revoked the moment it's replaced
      // — see discardPreviousSearch().
      this._searchToken = 0;
      this._currentObjectUrl = null;

      // "Take a photo" is offered on phones/tablets only — the shopper's own
      // camera is only really useful there. `pointer: coarse` reflects the
      // PRIMARY input, which distinguishes phones/tablets from laptops that
      // merely happen to have a webcam (getUserMedia support alone can't).
      var isTouchPrimary = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
      this.cameraSupported = isTouchPrimary && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      if (this.openCameraBtn) this.openCameraBtn.hidden = !this.cameraSupported;
      if (this.libraryLabelEl && this.cameraSupported) this.libraryLabelEl.textContent = 'Choose from library';

      this.querySelectorAll('[data-visual-search-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this._escHandler = (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      };
      document.addEventListener('keydown', this._escHandler);

      if (this.fileInput) {
        this.fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) this.handleFile(file);
        });
      }

      if (this.retakeBtn) {
        this.retakeBtn.addEventListener('click', () => this.reset());
      }

      if (this.openCameraBtn) {
        this.openCameraBtn.addEventListener('click', () => this.openCamera());
      }

      if (this.cameraCancelBtn) {
        this.cameraCancelBtn.addEventListener('click', () => this.closeCamera());
      }

      if (this.cameraShutterBtn) {
        this.cameraShutterBtn.addEventListener('click', () => {
          if (!this.cameraLocked) this.lockAndCapture();
        });
      }

      document.querySelectorAll('[data-visual-search-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._escHandler);
      this.closeCamera();
    }

    open() {
      this.hidden = false;
      // Hides the sitewide sticky collections bar (and anything else keyed
      // off this class) for as long as the modal is open — see the
      // "Suppress sticky page chrome" rule in abby-visual-search.css.
      document.documentElement.classList.add('visual-search-active');
      this._prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const self = this;
      // Two animation frames: the first lets the browser paint the
      // just-unhidden, not-yet-.active state; the second then flips the
      // class so the slide-in actually transitions instead of jumping.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          self.classList.add('active');
        });
      });
      const closeBtn = this.querySelector('.visual-search__close');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      this.classList.remove('active');
      document.documentElement.classList.remove('visual-search-active');
      document.body.style.overflow = this._prevOverflow || '';
      this.closeCamera();

      const self = this;
      let done = false;
      const fallback = setTimeout(finish, 300);
      function finish() {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        self.removeEventListener('transitionend', onTransitionEnd);
        self.hidden = true;
      }
      function onTransitionEnd(e) {
        if (e.target === self) finish();
      }
      this.addEventListener('transitionend', onTransitionEnd);
    }

    reset() {
      this.discardPreviousSearch();
      this.closeCamera();
      if (this.previewWrap) this.previewWrap.hidden = true;
      if (this.dropzone) this.dropzone.hidden = false;
      if (this.resultsEl) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
      }
      this.setPreviewLoading(false);
      this.setStatus('');
      if (this.fileInput) this.fileInput.value = '';
    }

    // Tears down whatever an earlier search left behind — its object URL,
    // its preview image, and (by advancing the token) any of its work still
    // in flight — so starting a new search never leaves the previous photo,
    // its results, or a late-arriving response meant for it lying around.
    // Called at the start of every new search and when the shopper backs
    // out via "choose a different photo".
    discardPreviousSearch() {
      this._searchToken = (this._searchToken || 0) + 1;
      if (this._currentObjectUrl) {
        URL.revokeObjectURL(this._currentObjectUrl);
        this._currentObjectUrl = null;
      }
      if (this.previewImg) this.previewImg.removeAttribute('src');
    }

    setStatus(text) {
      if (!this.statusEl) return;
      if (!text) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
      } else {
        this.statusEl.hidden = false;
        this.statusEl.textContent = text;
      }
    }

    setPreviewLoading(isLoading) {
      if (this.previewLoadingEl) this.previewLoadingEl.hidden = !isLoading;
    }

    // ---- Live camera ----

    openCamera() {
      if (!this.cameraSupported) return;
      if (this.dropzone) this.dropzone.hidden = true;
      if (this.cameraSection) this.cameraSection.hidden = false;

      this.cameraLocked = false;
      this.cameraStillCount = 0;
      this.cameraPrevLuma = null;
      this.setCameraError('');
      this.setCameraHint('Point at a product');
      if (this.cameraViewportEl) this.cameraViewportEl.classList.remove('is-locked');
      this.setBracketsVisible(false);

      const self = this;
      const attempts = [
        { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false },
        { video: true, audio: false }
      ];

      (function tryNext(i) {
        if (i >= attempts.length) {
          self.setCameraError("We couldn't access the camera. You can still choose a photo from your library.");
          return;
        }
        navigator.mediaDevices.getUserMedia(attempts[i]).then((stream) => {
          self.cameraStream = stream;
          if (self.videoEl) {
            self.videoEl.srcObject = stream;
            const playPromise = self.videoEl.play();
            if (playPromise && playPromise.catch) playPromise.catch(() => {});
          }
          self.startCameraLoop();
        }).catch(() => {
          tryNext(i + 1);
        });
      })(0);
    }

    closeCamera(returnToDropzone) {
      this.stopCameraLoop();
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((t) => t.stop());
        this.cameraStream = null;
      }
      if (this.videoEl) this.videoEl.srcObject = null;
      if (this.cameraSection) this.cameraSection.hidden = true;
      if (returnToDropzone !== false && this.dropzone) this.dropzone.hidden = false;
    }

    startCameraLoop() {
      this.stopCameraLoop();
      const self = this;
      this.cameraTimer = setInterval(() => self.cameraTick(), CAMERA_TICK_MS);
    }

    stopCameraLoop() {
      if (this.cameraTimer) {
        clearInterval(this.cameraTimer);
        this.cameraTimer = null;
      }
    }

    cameraTick() {
      const video = this.videoEl;
      if (!video || !video.videoWidth || this.cameraLocked) return;

      if (!this.cameraAnalysisCtx) {
        const c = document.createElement('canvas');
        c.width = CAMERA_ANALYSIS_W;
        c.height = CAMERA_ANALYSIS_H;
        this.cameraAnalysisCtx = c.getContext('2d', { willReadFrequently: true });
      }
      const ctx = this.cameraAnalysisCtx;
      const rect = coverCropRect(video.videoWidth, video.videoHeight, CAMERA_ANALYSIS_W / CAMERA_ANALYSIS_H);
      ctx.drawImage(video, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, CAMERA_ANALYSIS_W, CAMERA_ANALYSIS_H);

      let luma;
      try {
        luma = frameLuma(ctx, CAMERA_ANALYSIS_W, CAMERA_ANALYSIS_H);
      } catch (e) {
        return; // skip this tick rather than break the loop
      }

      let moving = true;
      if (this.cameraPrevLuma) {
        moving = meanAbsDiff(luma, this.cameraPrevLuma) > CAMERA_STILL_DIFF_THRESHOLD;
      }
      this.cameraPrevLuma = luma;

      const box = estimateForegroundBox(luma, CAMERA_ANALYSIS_W, CAMERA_ANALYSIS_H);

      if (moving) {
        this.cameraStillCount = 0;
        if (this.cameraViewportEl) this.cameraViewportEl.classList.remove('is-locked');
        this.setCameraHint('Point at a product');
        this.setBracketsVisible(!!box, box);
        return;
      }

      // Frame is steady. A confidently-detected box captures fast (the
      // common case, CAMERA_STILL_FRAMES_NEEDED). But this is only a
      // contrast heuristic against the frame's own edges — a white mug on a
      // light table, or a product filling the frame with no visible
      // background margin, never produces a box, and previously that meant
      // the scan just held here forever with no capture at all. Falling
      // back to a longer "steady with no box" timer (CAMERA_STILL_FRAMES_FALLBACK)
      // guarantees a capture still fires either way.
      this.cameraStillCount++;
      this.setBracketsVisible(!!box, box);
      this.setCameraHint('Hold still');

      const framesNeeded = box ? CAMERA_STILL_FRAMES_NEEDED : CAMERA_STILL_FRAMES_FALLBACK;
      if (this.cameraStillCount >= framesNeeded) {
        this.lockAndCapture();
      }
    }

    setBracketsVisible(visible, box) {
      const viewport = this.cameraViewportEl;
      if (!this.brackets || !viewport) return;
      const keys = ['tl', 'tr', 'bl', 'br'];

      if (!visible || !box) {
        keys.forEach((k) => {
          if (this.brackets[k]) this.brackets[k].classList.remove('is-visible');
        });
        return;
      }

      const w = viewport.clientWidth;
      const h = viewport.clientHeight;
      const bw = 28;
      const bh = 28;
      const left = box.x0 * w;
      const top = box.y0 * h;
      const right = box.x1 * w;
      const bottom = box.y1 * h;

      positionBracket(this.brackets.tl, left, top);
      positionBracket(this.brackets.tr, right - bw, top);
      positionBracket(this.brackets.bl, left, bottom - bh);
      positionBracket(this.brackets.br, right - bw, bottom - bh);

      keys.forEach((k) => {
        if (this.brackets[k]) this.brackets[k].classList.add('is-visible');
      });
    }

    setCameraHint(text) {
      if (this.cameraHintEl) this.cameraHintEl.textContent = text;
    }

    setCameraError(message) {
      if (!this.cameraErrorEl) return;
      this.cameraErrorEl.hidden = !message;
      this.cameraErrorEl.textContent = message || '';
    }

    lockAndCapture() {
      if (this.cameraLocked) return;
      this.cameraLocked = true;
      if (this.cameraViewportEl) this.cameraViewportEl.classList.add('is-locked');
      this.setCameraHint('Got it!');
      const self = this;
      setTimeout(() => self.captureFromVideo(), 220);
    }

    captureFromVideo() {
      const video = this.videoEl;
      if (!video || !video.videoWidth) {
        this.closeCamera();
        return;
      }
      const maxSide = 900; // plenty for a 96x96 feature vector; keeps the blob small
      const scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const self = this;
      canvas.toBlob((blob) => {
        self.closeCamera(false);
        if (self.dropzone) self.dropzone.hidden = false; // restored so "choose a different photo" works afterwards
        if (!blob) return;
        self.handleFile(blob);
      }, 'image/jpeg', 0.9);
    }

    // ---- Upload / search ----

    handleFile(file) {
      // Discard whatever the previous search left behind — its object URL,
      // its preview image, and anything of its still in flight — before
      // this one takes over, so the old photo never lingers alongside the
      // new one and a late-arriving response for it can never land on top
      // of these results.
      this.discardPreviousSearch();
      const token = this._searchToken;
      const isCurrent = () => token === this._searchToken;

      const objectUrl = URL.createObjectURL(file);
      this._currentObjectUrl = objectUrl;

      if (this.previewImg) this.previewImg.src = objectUrl;
      if (this.dropzone) this.dropzone.hidden = true;
      if (this.previewWrap) this.previewWrap.hidden = false;
      if (this.resultsEl) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
      }
      this.setPreviewLoading(true);

      this.setStatus('Looking at your photo…');

      loadImageElement(objectUrl).then((img) => {
        if (!isCurrent()) return;
        const queryVec = computeFeatureVector(toFeatureImageData(img));

        this.setStatus('Preparing visual search…');

        return ensureIndex(this.endpointUrl, this.staticIndexUrl, (done, total) => {
          if (!isCurrent()) return;
          this.setStatus('Preparing visual search… (' + done + '/' + total + ')');
        }).then((items) => {
          if (!isCurrent()) return;
          this.setStatus('Finding similar products…');

          const rankOutcome = rankByBestMatchThenSimilarity(items, queryVec);
          const ranked = rankOutcome.results;
          const isConfidentMatch = ranked.length &&
            rankOutcome.bestZScore >= VISUAL_MATCH_ZSCORE_THRESHOLD &&
            rankOutcome.bestScore >= VISUAL_MATCH_MIN_SCORE;

          if (isConfidentMatch) {
            this.renderResults(ranked);
            this.setStatus('');
            return;
          }

          // The top visual match doesn't stand out from the rest of the
          // catalog enough to trust as "the same item" — before giving up,
          // see if we can at least recognize what KIND of thing it is and
          // show real in-stock items from there.
          this.setStatus("We don't have an exact match — taking a closer look…");
          return this.tryAiCategoryFallback(img, queryVec).then((fallback) => {
            if (!isCurrent()) return;
            if (fallback && fallback.items.length) {
              this.renderFallbackResults(
                "We don't have an exact match — here's what's popular in " + fallback.label,
                fallback.items
              );
              this.setStatus('');
            } else if (ranked.length) {
              // Couldn't even place it in a recognizable category — show
              // the closest items we have, but honestly: no "Best match"
              // badge, since we already know this isn't a confident match.
              this.renderFallbackResults("The closest items we could find in our catalog", ranked);
              this.setStatus('');
            } else {
              this.setStatus("We couldn't find anything similar — try another photo.");
            }
          });
        });
      }).catch((err) => {
        if (!isCurrent()) return;
        console.error('[Abby visual search]', err);
        this.setStatus('Something went wrong reading that photo — please try another.');
      }).then(() => {
        if (isCurrent()) this.setPreviewLoading(false);
        URL.revokeObjectURL(objectUrl);
        if (this._currentObjectUrl === objectUrl) this._currentObjectUrl = null;
      });
    }

    // Runs only when the plain visual match was weak. Classifies the query
    // photo with a small on-device model, maps the guess to one of our real
    // collections, fetches that collection's actual products, and re-ranks
    // THOSE by visual similarity to the query photo — so the result is both
    // "the right general kind of thing" and, among those, the closest-
    // looking ones we have. Always resolves (never rejects): any failure
    // along the way — model load, no keyword match, empty collection,
    // network hiccup — just means no fallback results, and the caller falls
    // back again to the plain visual-only ranking.
    tryAiCategoryFallback(img, queryVec) {
      const self = this;
      return ensureAiModel().then((model) => {
        return model.classify(img, AI_CATEGORY_TOPK);
      }).then((predictions) => {
        const match = mapPredictionsToCategory(predictions);
        if (!match) return null;

        self.setStatus('Looking for ' + match.label + ' we carry…');

        return fetchCollectionProducts(self.rootUrl, match.collection, CATEGORY_RESULT_COUNT * CATEGORY_FETCH_MULTIPLIER).then((rawProducts) => {
          if (!rawProducts.length) return null;

          const scored = [];
          const work = rawProducts.map((raw) => {
            const item = normalizeShopifyProduct(raw);
            if (!item.image) return Promise.resolve();
            return loadImageElement(item.image).then((candidateImg) => {
              const vec = computeFeatureVector(toFeatureImageData(candidateImg));
              scored.push({ it: item, score: dotProduct(queryVec, vec) });
            }).catch(() => {}); // a single broken product photo shouldn't sink the whole fallback
          });

          return Promise.all(work).then(() => {
            scored.sort((a, b) => b.score - a.score);
            return {
              label: match.label,
              items: scored.slice(0, CATEGORY_RESULT_COUNT)
            };
          });
        });
      }).catch((err) => {
        console.warn('[Abby visual search] category fallback unavailable:', err);
        return null;
      });
    }

    renderResults(results) {
      if (!this.resultsEl) return;
      if (!results.length) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
        return;
      }
      const root = this.rootUrl;

      function card(entry) {
        const it = entry.it;
        const href = root + '/products/' + it.handle;
        if (entry.isBest) {
          return (
            '<a class="visual-search__result visual-search__result--best" href="' + href + '">' +
              '<span class="visual-search__result-image">' +
                '<img src="' + it.image + '" alt="" loading="lazy" width="128" height="128">' +
              '</span>' +
              '<span class="visual-search__result-body">' +
                '<span class="visual-search__result-badge">Best match</span>' +
                '<span class="visual-search__result-title">' + escapeHtml(it.title) + '</span>' +
                '<span class="visual-search__result-price">' + escapeHtml(it.price) + '</span>' +
              '</span>' +
            '</a>'
          );
        }
        return (
          '<a class="visual-search__result" href="' + href + '">' +
            '<span class="visual-search__result-image">' +
              '<img src="' + it.image + '" alt="" loading="lazy" width="128" height="128">' +
            '</span>' +
            '<span class="visual-search__result-title">' + escapeHtml(it.title) + '</span>' +
            '<span class="visual-search__result-price">' + escapeHtml(it.price) + '</span>' +
          '</a>'
        );
      }

      let html = card(results[0]);
      if (results.length > 1) {
        html += '<p class="visual-search__results-heading">More like this</p>';
        for (let i = 1; i < results.length; i++) html += card(results[i]);
      }
      this.resultsEl.innerHTML = html;
      this.resultsEl.hidden = false;
    }

    // Renders any of the fallback result sets — plain product cards, with
    // no "Best match" hero treatment, under a heading that says plainly
    // that these are a guess (a category classification, or just "the
    // closest we could find") rather than a confident visual match. Used
    // both for the AI category fallback and for the last-resort case where
    // even that can't recognize the photo.
    renderFallbackResults(headingText, items) {
      if (!this.resultsEl) return;
      if (!items.length) {
        this.resultsEl.hidden = true;
        this.resultsEl.innerHTML = '';
        return;
      }
      const root = this.rootUrl;

      function card(entry) {
        const it = entry.it;
        const href = root + '/products/' + it.handle;
        return (
          '<a class="visual-search__result" href="' + href + '">' +
            '<span class="visual-search__result-image">' +
              '<img src="' + it.image + '" alt="" loading="lazy" width="128" height="128">' +
            '</span>' +
            '<span class="visual-search__result-title">' + escapeHtml(it.title) + '</span>' +
            '<span class="visual-search__result-price">' + escapeHtml(it.price) + '</span>' +
          '</a>'
        );
      }

      let html = '<p class="visual-search__results-heading visual-search__results-heading--fallback">' +
        escapeHtml(headingText) +
        '</p>';
      for (let i = 0; i < items.length; i++) html += card(items[i]);

      this.resultsEl.innerHTML = html;
      this.resultsEl.hidden = false;
    }
  }

  if (!window.customElements.get('visual-search-modal')) {
    window.customElements.define('visual-search-modal', VisualSearchModal);
  }

  // Internal tool (see page.abby-visual-search-build.liquid): builds the full index
  // once in a real browser — with no sandbox/network restrictions of any kind — and
  // offers it as a downloadable file. That file gets shipped as a static theme asset
  // so ordinary shoppers never have to download and analyze every product photo
  // themselves. Does nothing unless the builder page's button is present on the page.
  function initIndexBuilder() {
    var btn = document.getElementById('abby-build-index-btn');
    if (!btn) return;

    var statusEl = document.getElementById('abby-build-status');
    var downloadWrap = document.getElementById('abby-build-download-wrap');
    var endpointUrl = btn.getAttribute('data-endpoint-url');

    function setStatus(text) {
      if (statusEl) statusEl.textContent = text;
    }

    btn.addEventListener('click', function () {
      btn.disabled = true;
      downloadWrap.innerHTML = '';
      setStatus('Starting…');

      fetchJson(endpointUrl).then(function (firstPage) {
        return fetchFullCatalog(endpointUrl, firstPage).then(function (products) {
          setStatus('Found ' + products.length + ' products. Downloading and analyzing photos — keep this tab open…');

          return buildIndexFromCatalog(products, function (done, total, failedSoFar) {
            setStatus('Processed ' + done + ' of ' + total + ' (' + failedSoFar + ' failed so far)…');
          }).then(function (result) {
            var rounded = result.items.map(function (it) {
              return {
                id: it.id,
                title: it.title,
                handle: it.handle,
                price: it.price,
                image: it.image,
                type: it.type,
                vec: it.vec.map(function (n) { return Math.round(n * 10000) / 10000; })
              };
            });
            var payload = { total: firstPage.total, builtAt: Date.now(), items: rounded };
            var json = JSON.stringify(payload);

            setStatus('Done: ' + result.items.length + ' succeeded, ' + result.failed + ' failed, out of ' + firstPage.total + ' products total.');

            var blob = new Blob([json], { type: 'application/json' });
            var url = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = url;
            link.download = 'abby-visual-search-index.json';
            link.textContent = 'Download index file (' + Math.round(json.length / 1024) + ' KB)';
            link.style.display = 'inline-block';
            link.style.marginTop = '12px';
            link.style.fontSize = '17px';
            downloadWrap.appendChild(link);

            if (result.failed > 0) {
              var warn = document.createElement('p');
              warn.style.color = '#b00020';
              warn.textContent = result.failed + ' product photo(s) could not be analyzed by this browser and will be missing from search results.';
              downloadWrap.appendChild(warn);
            }

            btn.disabled = false;
            btn.textContent = 'Rebuild index';
          });
        });
      }).catch(function (err) {
        setStatus('Something went wrong: ' + (err && err.message ? err.message : err));
        btn.disabled = false;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initIndexBuilder);
  } else {
    initIndexBuilder();
  }
})();
